// packages/shared-components/src/hooks/useKeyboardLighting.ts

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { KeyboardSDK } from "../types/sdk";
import type {
  KeyLightingConfig,
  KeyboardType,
} from "../components/Keyboard/types";
import {
  customDataToKeyColors,
  expandDeep80MaxSdkLightingGridToUi,
  expandDk80SdkLightingGridToUi,
  getPositionMapByType,
  isXingshanDk80Device,
  usesDeep80MaxCompactSdkGrid,
  readActiveEquipmentConfiguration,
  transformXingshanEs68FamilyLightingGrid,
  transformXingshanX87HeLightingGrid,
} from "../utils";
import { isX75v3KeyboardType } from "../utils/sliceFirstRowKeyboardMatrix";
import {
  ACTIVE_DRIVER_TAB_CHANGED_EVENT,
  isActiveDriverTab,
  isBeiyingDevice8815X98Keyboard,
  isXingshanDoubleLightingEquipment,
} from "@eweadn-qiankun/shared-utils";
import { buildKeyColorsFromXingshanLightingGrid } from "../utils/xingshanDoubleLightingGrid";
import {
  useOwnerResyncParticipant,
  fingerprintJson,
  isOwnerResyncLoading,
} from "../utils/deviceSessionOwnerResync";
// import { getStoredTheme } from "@eweadn-qiankun/shared-theme";

export interface UseKeyboardLightingOptions {
  sdkApi?: KeyboardSDK;
  keyboardType?: KeyboardType;
  currentDeviceType?: string;
  enabled?: boolean;
  pollingInterval?: number;
  /** SDK 就绪后再额外等待的毫秒数，再启动轮询（灯光页首进建议 200） */
  pollStartupDelayMs?: number;
  onSuccess?: (keyColors: Record<string, KeyLightingConfig>) => void;
  onError?: (error: Error) => void;
}

const SDK_API_WAIT_INTERVAL_MS = 50;
const SDK_API_WAIT_TIMEOUT_MS = 5000;
const LIGHTING_FETCH_TIMEOUT_MS = 5000;
const BEIYING_24G_LIGHTING_FETCH_TIMEOUT_MS = 15_000;
const BEIYING_24G_MIN_POLLING_INTERVAL_MS = 3000;

function isBeiyingWirelessLink(ed: Record<string, unknown> | null): boolean {
  if (!ed) return false;
  return ed.is4G === true || ed.workMode === 2 || ed.workMode === "2";
}

/**
 * 贝盈 2.4G：HID 往返慢，需拉长灯光轮询超时/间隔。
 * 覆盖 ES87（web-hid）与 X98 1K（DeviceWireLess8815）。
 */
function isBeiyingWirelessSlowLightingEquipment(): boolean {
  const ed = readActiveEquipmentConfiguration();
  if (!ed || String(ed.solutionRovider ?? "") !== "beiying") return false;
  if (!isBeiyingWirelessLink(ed)) return false;
  const kt = String(ed.keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (kt === "es87") return true;
  return isBeiyingDevice8815X98Keyboard(ed);
}

function resolveLightingFetchTimeoutMs(): number {
  return isBeiyingWirelessSlowLightingEquipment()
    ? BEIYING_24G_LIGHTING_FETCH_TIMEOUT_MS
    : LIGHTING_FETCH_TIMEOUT_MS;
}

/** 仅等待 lighting API 注入 + 固定延迟，不主动打 SDK（避免与 getLightingBase 并发） */
async function waitBeforeLightingPoll(
  getSdkApi: () => KeyboardSDK | undefined,
  startupDelayMs: number
): Promise<void> {
  const deadline = Date.now() + SDK_API_WAIT_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (getSdkApi()?.sdkKeyboardLightingApi) break;
    await new Promise((r) => setTimeout(r, SDK_API_WAIT_INTERVAL_MS));
  }
  if (startupDelayMs > 0) {
    await new Promise((r) => setTimeout(r, startupDelayMs));
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("lighting fetch timeout"));
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

export interface UseKeyboardLightingResult {
  keyColors: Record<string, KeyLightingConfig>;
  isLoading: boolean;
  /** `force`: 与轮询并发时仍等待执行（避免切自定义后立即 `refresh` 被 `isFetchingRef` 吞掉） */
  refresh: (
    lightingMode?: any,
    opts?: { force?: boolean; silent?: boolean }
  ) => Promise<void | Record<string, KeyLightingConfig> | null>;
  handleVisibilityChange: () => void;
}

export function useKeyboardLighting(
  options: UseKeyboardLightingOptions = {}
): UseKeyboardLightingResult {
  const {
    sdkApi,
    keyboardType = "68",
    enabled = true,
    pollingInterval = 2000,
    pollStartupDelayMs = 0,
    currentDeviceType,
    onSuccess,
    onError,
  } = options;

  const location = useLocation();
  const effectiveDeviceType = (() => {
    const fromProp = String(currentDeviceType ?? "").trim();
    if (fromProp) return fromProp;
    try {
      const fromLs = String(
        localStorage.getItem("currentDeviceType") ?? ""
      ).trim();
      if (fromLs) return fromLs;
    } catch {
      /* ignore */
    }
    return "";
  })();
  const [keyColors, setKeyColors] = useState<Record<string, KeyLightingConfig>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const pollingTimeoutIdsRef = useRef<number[]>([]);
  const isMountedRef = useRef(true);

  const effectiveKeyboardType = (() => {
    const kb = String(keyboardType ?? "")
      .trim()
      .toLowerCase();
    if (kb === "x98_1k") return "X98_1K" as KeyboardType;

    /** 与 main-app `dataConversion.isBeiying8815KeyboardEquipment` 对齐，用于灯光矩阵与 8815 SDK 一致 */
    const isBeiyingX98815FromStorage = (): boolean => {
      const ed = readActiveEquipmentConfiguration();
      if (!ed) return false;
      try {
        const display = String(ed?.displayname ?? "")
          .trim()
          .toLowerCase();
        const kt = String(ed?.keyboardType ?? "")
          .trim()
          .toLowerCase();
        if (kt === "m80" || kt === "m71" || kt === "t87" || kt === "x75v2")
          return false;
        if (kt === "x98_1k") return true;
        if (
          String(ed?.solutionRovider ?? "") === "beiying" &&
          kt === "98" &&
          display === "x98 (1k) touch"
        ) {
          return true;
        }
        if (
          (display === "x98 (1k) touch" || kt === "x98_1k") &&
          kt !== "m80" &&
          kt !== "m71" &&
          kt !== "t87"
        ) {
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    };

    const configurationNow = readActiveEquipmentConfiguration();
    if (configurationNow) {
      try {
        const displayname = String(configurationNow.displayname ?? "")
          .trim()
          .toLowerCase();
        if (displayname === "x98 (1k) touch" && kb === "98") {
          return "X98_1K" as KeyboardType;
        }
      } catch {
        /* ignore invalid cache */
      }
    }
    if (kb === "98" && isBeiyingX98815FromStorage()) {
      return "X98_1K" as KeyboardType;
    }
    return keyboardType;
  })();
  /** x75v3 灯光矩阵列与静态 KEYBOARD_LAYOUTS 压紧下标不一致，仅依赖 lightingMatrixKey */
  const positionMap = isX75v3KeyboardType(effectiveKeyboardType)
    ? {}
    : getPositionMapByType(effectiveKeyboardType);
  const ALLOW_PATH_ARRAY = [
    "/keyboard-management/lighting",
    "/keyboard-management/musicDance",
  ];

  const lightingModeRef = useRef(undefined);

  // 组件挂载状态管理
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 方案商灯光数据处理
  const getSolutionRoviderLighting = useCallback((data: any) => {
    const newData = JSON.parse(JSON.stringify(data));
    const configurationNow = readActiveEquipmentConfiguration();
    if (!configurationNow) {
      return newData;
    }
    const productId = configurationNow.productId as number | undefined;
    const vendorId = configurationNow.vendorId as number | undefined;
    const solutionRovider = configurationNow.solutionRovider as
      | string
      | undefined;
    if (solutionRovider === "xingshan") {
      if (productId === 7178 && vendorId === 7334) {
        return transformXingshanX87HeLightingGrid(newData, {
          solutionRovider,
          productId,
          vendorId,
        });
      } else if (usesDeep80MaxCompactSdkGrid(productId, vendorId)) {
        return expandDeep80MaxSdkLightingGridToUi(newData);
      } else if (isXingshanDk80Device(productId, vendorId)) {
        return expandDk80SdkLightingGridToUi(newData);
      } else {
        return transformXingshanEs68FamilyLightingGrid(newData, {
          solutionRovider,
          productId,
          vendorId,
        });
      }
    }
    return newData;
  }, []);

  const sdkApiRef = useRef(sdkApi);
  sdkApiRef.current = sdkApi;

  const fetchLightingData = useCallback(
    async (
      lightingMode?: any,
      opts?: { force?: boolean; silent?: boolean }
    ): Promise<Record<string, KeyLightingConfig> | null | void> => {
      lightingModeRef.current = lightingMode;
      const silent = opts?.silent === true;
      const lightingApi = sdkApiRef.current?.sdkKeyboardLightingApi;
      if (
        !lightingApi ||
        location.pathname !== "/keyboard-management/lighting"
      ) {
        return null;
      }

      const force = opts?.force === true;
      if (isFetchingRef.current) {
        if (!force && !silent) return;
        const deadline = Date.now() + 3000;
        while (isFetchingRef.current && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 40));
        }
        if (isFetchingRef.current) return null;
      }

      isFetchingRef.current = true;
      if (!silent && !isOwnerResyncLoading()) {
        setIsLoading(true);
      }

      try {
        const equipment = readActiveEquipmentConfiguration();
        if (String(equipment?.solutionRovider ?? "") === "rongyuan") {
          return null;
        }

        const response = await withTimeout(
          lightingApi.getLightingCustom(),
          resolveLightingFetchTimeoutMs()
        );
        if (response.code === 200 && response.data) {
          const res: any = getSolutionRoviderLighting(response.data);
          const equipment = readActiveEquipmentConfiguration();
          const newKeyColors = isXingshanDoubleLightingEquipment(equipment)
            ? buildKeyColorsFromXingshanLightingGrid(
                res,
                String(lightingModeRef.current ?? "Open"),
                positionMap
              )
            : customDataToKeyColors(res, positionMap);
          if (silent) {
            return newKeyColors;
          }
          if (isMountedRef.current) {
            if (lightingModeRef.current !== "Close") {
              setKeyColors(newKeyColors);
              onSuccess?.(newKeyColors);
            } else {
              let globalStore: any = localStorage.getItem("global-store");
              if (globalStore) {
                globalStore = JSON.parse(globalStore);
                const theme = globalStore.state.theme;
                let color = {
                  r: 235,
                  g: 237,
                  b: 240,
                };
                if (theme === "dark") {
                  color = {
                    r: 6,
                    g: 8,
                    b: 10,
                  };
                }
                for (const key in newKeyColors) {
                  newKeyColors[key].R = color.r;
                  newKeyColors[key].G = color.g;
                  newKeyColors[key].B = color.b;
                }
                setKeyColors(newKeyColors);
                onSuccess?.(newKeyColors);
              }
            }
          }
        }
        return null;
      } catch (error: any) {
        if (!silent) {
          console.error("获取灯光配置失败:", error);
          onError?.(error);
        }
        return null;
      } finally {
        if (isMountedRef.current) {
          isFetchingRef.current = false;
          if (!silent && !isOwnerResyncLoading()) {
            setIsLoading(false);
          }
        }
      }
    },
    [positionMap, onSuccess, onError, location.pathname]
  );

  const fetchLightingDataRef = useRef(fetchLightingData);
  fetchLightingDataRef.current = fetchLightingData;

  const scheduleNextFetchRef = useRef<(() => void) | null>(null);
  scheduleNextFetchRef.current = () => {
    if (
      !ALLOW_PATH_ARRAY.includes(location.pathname) ||
      (typeof window !== "undefined" && window.__eweadn_deviceSessionStandby) ||
      !isActiveDriverTab()
    ) {
      return;
    }

    pollingTimeoutIdsRef.current.forEach((id) => clearTimeout(id));
    pollingTimeoutIdsRef.current.length = 0;

    void (async () => {
      try {
        if (!isFetchingRef.current) {
          await fetchLightingDataRef.current(lightingModeRef.current);
        }
      } catch {
        /* 超时或 SDK 异常：下一轮继续 */
      } finally {
        if (
          isMountedRef.current &&
          ALLOW_PATH_ARRAY.includes(location.pathname) &&
          !(
            typeof window !== "undefined" &&
            window.__eweadn_deviceSessionStandby
          ) &&
          isActiveDriverTab()
        ) {
          const intervalMs = isBeiyingWirelessSlowLightingEquipment()
            ? Math.max(pollingInterval, BEIYING_24G_MIN_POLLING_INTERVAL_MS)
            : pollingInterval;
          const timerId = setTimeout(() => {
            scheduleNextFetchRef.current?.();
          }, intervalMs) as unknown as number;
          pollingTimeoutIdsRef.current.push(timerId);
        }
      }
    })();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden" || !isActiveDriverTab()) {
      pollingTimeoutIdsRef.current.forEach((id) => clearTimeout(id));
      pollingTimeoutIdsRef.current.length = 0;
    } else if (document.visibilityState === "visible" && isActiveDriverTab()) {
      scheduleNextFetchRef.current?.();
    }
  };

  useEffect(() => {
    if (
      !enabled ||
      !effectiveDeviceType ||
      !ALLOW_PATH_ARRAY.includes(location.pathname)
    ) {
      return;
    }

    let cancelled = false;
    let standby = Boolean(
      typeof window !== "undefined" && window.__eweadn_deviceSessionStandby
    );

    const onStandbyChange = (ev: Event) => {
      standby = Boolean(
        (ev as CustomEvent<{ standby?: boolean }>).detail?.standby
      );
      if (standby) {
        pollingTimeoutIdsRef.current.forEach((id) => clearTimeout(id));
        pollingTimeoutIdsRef.current.length = 0;
      }
    };

    window.addEventListener(
      "eweadn:deviceSessionStandbyChanged",
      onStandbyChange
    );

    void (async () => {
      if (standby) return;
      const startupDelayMs = isBeiyingWirelessSlowLightingEquipment()
        ? Math.max(pollStartupDelayMs, 1500)
        : pollStartupDelayMs;
      await waitBeforeLightingPoll(() => sdkApiRef.current, startupDelayMs);
      if (cancelled || !isMountedRef.current || standby) return;

      scheduleNextFetchRef.current?.();
    })();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(
      ACTIVE_DRIVER_TAB_CHANGED_EVENT,
      handleVisibilityChange as EventListener
    );

    return () => {
      cancelled = true;
      window.removeEventListener(
        "eweadn:deviceSessionStandbyChanged",
        onStandbyChange
      );
      pollingTimeoutIdsRef.current.forEach((id) => clearTimeout(id));
      pollingTimeoutIdsRef.current.length = 0;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(
        ACTIVE_DRIVER_TAB_CHANGED_EVENT,
        handleVisibilityChange as EventListener
      );
    };
  }, [
    enabled,
    pollingInterval,
    pollStartupDelayMs,
    effectiveDeviceType,
    location.pathname,
    sdkApi?.sdkKeyboardLightingApi,
  ]);

  const lightingPollEnabled =
    enabled &&
    !!effectiveDeviceType &&
    ALLOW_PATH_ARRAY.includes(location.pathname);

  const probedKeyColorsRef = useRef<Record<string, KeyLightingConfig> | null>(
    null
  );

  useOwnerResyncParticipant(
    {
      id: "keyboard-lighting-colors",
      enabled: () => lightingPollEnabled,
      probe: async () => {
        await waitBeforeLightingPoll(
          () => sdkApiRef.current,
          pollStartupDelayMs
        );
        const colors = (await fetchLightingDataRef.current(
          lightingModeRef.current,
          { force: true, silent: true }
        )) as Record<string, KeyLightingConfig> | null | void;
        if (!colors || !Object.keys(colors).length) {
          return { valid: false, fingerprint: "" };
        }
        probedKeyColorsRef.current = colors;
        return { valid: true, fingerprint: fingerprintJson(colors) };
      },
      commit: async () => {
        const cached = probedKeyColorsRef.current;
        probedKeyColorsRef.current = null;
        if (cached && isMountedRef.current) {
          setKeyColors(cached);
          onSuccess?.(cached);
        } else {
          await fetchLightingDataRef.current(lightingModeRef.current, {
            force: true,
          });
        }
        scheduleNextFetchRef.current?.();
      },
    },
    lightingPollEnabled
  );

  return {
    keyColors,
    isLoading,
    refresh: fetchLightingData,
    handleVisibilityChange,
  };
}
